<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { getDifference } from "@/common/utils/dates";

import {
  analysisCreate,
  analysisCopy,
  analysisDelete,
  analysisList,
} from "@/common/api/rest/analysis/analysis";

import type { AnalysisListType } from "@/common/types/analysis";
import type { EvaluationType } from "@/common/types/evaluation";

import { analysisHome } from "@/components";
import {
  baseConfirm,
  baseLoading,
  baseInput,
  baseTooltip,
  svgIcon,
  cardAnalysis,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data", "selected-code"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const observerRef = ref();
const intersectionObserver = ref<IntersectionObserver | null>(null);
const isObserverVisible = ref(false);

const analysis = ref<AnalysisListType[]>([]);
const selected = ref<AnalysisListType | null>(null);
const tab = ref<string>("PENDING");

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const searchInput = ref<string>("");
const count = ref<number>(0);
const total = ref<number>(0);

const loading = ref(false);
const hasMore = ref(true);
const analysisEdit = ref(false);
const showAddConfirm = ref(false);
const showDelConfirm = ref(false);
const showCopyConfirm = ref(false);

function resetPagination() {
  analysis.value = [];
  limit.value = 10;
  page.value = 0;
  searchInput.value = "";
  hasMore.value = true;
}

function fetchData() {
  if (!props.data) return;

  loading.value = true;

  analysisList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: searchInput.value,
    status: tab.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        analysis.value = Array.from(new Set([...analysis.value, ...data.list]));
        count.value = data.infos.count;
        total.value = data.infos.total;
        pages.value = data.infos.pages;
        if (data.list.length === 0) {
          hasMore.value = false;
        }
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;

      // O IntersectionObserver não dispara de novo se o elemento
      // permanece visível. Então, após cada load, reavaliamos manualmente.
      nextTick(() => {
        if (
          isObserverVisible.value &&
          hasMore.value &&
          page.value + 1 < pages.value
        ) {
          tryLoadMore();
        }
      });
    });
}

function tryLoadMore() {
  if (!isObserverVisible.value || !hasMore.value || loading.value) return;

  const nextPage = page.value + 1;

  if (nextPage >= pages.value) {
    hasMore.value = false;
    return;
  }

  page.value = nextPage;
  fetchData();
}

function update() {
  emit("update-data");
}

function add() {
  showAddConfirm.value = true;
}

function addCancel() {
  showAddConfirm.value = false;
}

function addConfirm() {
  if (!props.data) return;

  showAddConfirm.value = false;

  analysisCreate(props.data.account_id, props.data.evaluation_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        resetPagination();
        fetchData();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function edit(data: AnalysisListType) {
  selected.value = data;
  analysisEdit.value = true;
  emit("selected-code", data.code);
}

function editCancel() {
  selected.value = null;
  analysisEdit.value = false;
  fetchData();
}

function del(data: AnalysisListType) {
  selected.value = data;
  showDelConfirm.value = true;
}

function delCancel() {
  showDelConfirm.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!selected.value) return;

  showDelConfirm.value = false;

  analysisDelete(selected.value.account_id, selected.value.analysis_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        resetPagination();
        delCancel();
        fetchData();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function clearSearch() {
  if (searchInput.value?.trim()?.length > 0) return;

  searchInput.value = "";
  fetchData();
}

function cleanList() {
  hasMore.value = true;
  analysis.value = [];
  page.value = 0;
}

function changeTab(value: string) {
  tab.value = value;
  page.value = 0;
  analysis.value = [];
  hasMore.value = true;
  fetchData();
}

function copy(data: AnalysisListType) {
  selected.value = data;
  showCopyConfirm.value = true;
}

function copyCancel() {
  selected.value = null;
  showCopyConfirm.value = false;
}

async function copyConfirm() {
  if (!selected.value) return;

  analysisCopy(selected.value.account_id, selected.value.analysis_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        toast({ message: message, type: "success" });
        resetPagination();
        fetchData();
        copyCancel();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onUnmounted(() => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
  }
});

onMounted(() => {
  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      isObserverVisible.value = entries[0].isIntersecting;

      if (entries[0].isIntersecting) {
        tryLoadMore();
      }
    },
    {
      rootMargin: "200px",
    },
  );

  if (observerRef.value) {
    intersectionObserver.value.observe(observerRef.value);
  }

  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="!analysisEdit">
      <template v-if="showTitle">
        <div class="title">
          {{ t("common.labels.analysis") }}
        </div>
      </template>

      <div class="content">
        <div class="tabs">
          <div
            class="tab"
            :class="{
              active: tab === 'PENDING',
            }"
            @click="changeTab('PENDING')"
          >
            PENDENTES
          </div>
          <div
            class="tab"
            :class="{
              active: tab === 'PROGRESS',
            }"
            @click="changeTab('PROGRESS')"
          >
            INICIADAS
          </div>
          <div
            class="tab"
            :class="{
              active: tab === 'CONCLUDED',
            }"
            @click="changeTab('CONCLUDED')"
          >
            FINALIZADAS
          </div>
        </div>

        <div class="header">
          <div class="search">
            <base-input
              v-model="searchInput"
              background="#fff"
              color="#000"
              :placeholder="t('common.labels.search')"
              @update:model-value="clearSearch()"
              @key:enter="
                cleanList();
                fetchData();
              "
            >
              <template #sufix>
                <div class="icon">
                  <svg-icon
                    icon="search"
                    size="1.75rem"
                    @click="
                      cleanList();
                      fetchData();
                    "
                  />
                </div>
              </template>
            </base-input>
          </div>

          <div class="actions">
            <button class="btn btn-md btn-primary" @click="add()">
              <svg-icon icon="add" size="1.1rem" />
              <span>
                {{ t("common.labels.add")?.toUpperCase() }}
              </span>
            </button>
          </div>
        </div>

        <div class="list">
          <template v-for="item in analysis" :key="item.analysis_id">
            <card-analysis
              :data="item"
              @edit="edit(item)"
              @copy="copy(item)"
              @delete="del(item)"
            />
          </template>

          <template v-if="loading">
            <div class="flex items-center justify-center">
              <base-loading />
            </div>
          </template>

          <div ref="observerRef" class="observer"></div>
        </div>
      </div>
    </template>

    <template v-else>
      <template v-if="showTitle">
        <div class="title">
          <div class="flex flex-row flex-1 justify-between">
            <span>
              {{ t("common.labels.analyse") }} - {{ selected?.code }}
            </span>
            <base-tooltip>
              <template #trigger>
                <button class="btn btn-back btn-sm" @click="editCancel()">
                  <svg-icon icon="close" size="1.25rem" />
                </button>
              </template>
              <template #tooltip>
                <div class="close">
                  {{ t("common.labels.close") }}
                </div>
              </template>
            </base-tooltip>
          </div>
        </div>
      </template>

      <template v-if="selected">
        <analysis-home
          :data="selected"
          :evaluation-code="data?.code ?? ''"
          :company="data?.company ?? null"
          :address-id="data?.address_id ?? null"
          :is-edit="isEdit"
          @update-data="fetchData()"
        />
      </template>
    </template>

    <base-confirm
      :show="showAddConfirm"
      max-width="400px"
      :title="t('evaluation.modal.titles.add_analysis').toUpperCase()"
      :message="t('evaluation.modal.messages.add_analysis')"
      @cancel="addCancel()"
      @confirm="addConfirm()"
    />

    <base-confirm
      :show="showCopyConfirm"
      max-width="400px"
      :title="
        t('common.labels.copy', {
          concat: t('common.labels.analyse'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.copy_message', {
          concat: `<strong>${selected?.code}</strong>`,
        })
      "
      @cancel="copyCancel()"
      @confirm="copyConfirm()"
    />

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.analyse'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: t('common.labels.analyse'),
        })
      "
      @cancel="delCancel()"
      @confirm="delConfirm()"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }

  .close {
    text-transform: none;
    font-weight: normal;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .tabs {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      width: 100%;
      gap: 2px;

      .tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--white-off);
        padding-inline: 1rem;
        padding-block: 0.5rem;
        color: var(--text-off);
        user-select: none;
        cursor: pointer;

        &.active {
          background: var(--primary);
          color: var(--white);
        }
      }
    }

    .header {
      display: grid;
      grid-template-columns: 2fr 1fr;
      justify-content: space-between;
      gap: 1rem;
      height: 2.9rem;

      .search {
        width: 100%;

        .icon {
          cursor: pointer;
          color: var(--text-off);

          &:hover {
            color: var(--primary);
          }
        }
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
      }
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .infos {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-block-start: 1.5rem;
        padding-block-end: 0.7rem;
        font-size: 0.95rem;
      }
    }
  }
}
</style>
